import { useCallback, useMemo } from 'react'
import { usePTCreationPermission } from '../components/TreeView/hooks/usePTCreationPermission'
import { useNodeMapShallowSelector } from '../features/keyOperations'
import {
    isDataNodeMOPTArticle,
    useMOPTDialogActions,
} from '../features/moptArticleRestriction'
import { DataNode } from '../types/types'
import { useShallowSelector } from '../Workspace2Store'
import { useArticleActions } from './useArticleActions'
import { useParagraphActions } from './useParagraphActions'

/** Constants for tooltip text messages */
const TOOLTIP_TEXT = {
    ADD_CONTENT: 'Add Content',
    PT_CREATION_NOT_ALLOWED: 'Adding content is not available',
    COMPLETE_CURRENT_CONTENT: 'Complete current content before adding new',
    ADD_CONTENT_NOT_ALLOWED: 'Operation not allowed',
    MO_PT_ARTICLE_RESTRICTION:
        'Cannot add content under Manufacturer Option articles',
} as const

/**
 * Pure function to determine tooltip text based on state
 * Extracted to avoid unnecessary re-renders
 */
const getTooltipTextForState = (
    isAddDisabled: boolean,
    hasContentBeingAdded: boolean,
    isPTCreationAllowed: boolean,
    isMOPTArticle: boolean
): string => {
    if (!isAddDisabled) return TOOLTIP_TEXT.ADD_CONTENT
    if (isMOPTArticle) return TOOLTIP_TEXT.MO_PT_ARTICLE_RESTRICTION
    if (hasContentBeingAdded) return TOOLTIP_TEXT.COMPLETE_CURRENT_CONTENT
    if (!isPTCreationAllowed) return TOOLTIP_TEXT.PT_CREATION_NOT_ALLOWED
    return TOOLTIP_TEXT.ADD_CONTENT_NOT_ALLOWED
}

/**
 * Interface for the useAddContentActions hook return value
 */
export interface UseAddContentActionsReturn {
    /** Function to add content after a sibling node */
    addContentAfterSibling: (sibling: DataNode) => void
    /** Check if content can be added after a sibling node */
    canAddContent: (
        sibling: DataNode,
        from?: 'AddIcon' | 'KeyboardShortcut'
    ) => boolean
    /** Whether adding content is currently disabled */
    isAddDisabled: boolean
    /** Whether content is currently being added or saved */
    hasContentBeingAdded: boolean
    /** Get appropriate tooltip text based on current state */
    getTooltipText: (node?: DataNode) => string
}

/**
 * Reusable hook for adding content after sibling nodes.
 * Provides the same functionality as AddIcon's onClick behavior.
 * Used by both AddIcon component and keyboard shortcuts.
 */
export const useAddContentActions = (): UseAddContentActionsReturn => {
    const { addArticleAfterSibling } = useArticleActions()
    const {
        addParagraphAfterSibling,
        addFirstParagraphToArticle,
    } = useParagraphActions()
    const ptCreation = usePTCreationPermission()
    const { setDialogOpen: setMOPTDialogOpen } = useMOPTDialogActions()
    const {
        isNodeInProject,
        parentTreePath,
        textContentElementToAdd,
        expandedKeys,
    } = useShallowSelector((state) => ({
        isNodeInProject: state.selectedProjectElementNode?.isInProject,
        parentTreePath: state.selectedProjectElementNode?.parentTreePath,
        textContentElementToAdd: state.textContentElementToAdd,
        expandedKeys: state.expandedKeys,
    }))
    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )
    const hasContentBeingAdded =
        !!textContentElementToAdd || isSaveOperationInProgress

    const isAddDisabled = useMemo(
        () => !isNodeInProject || !ptCreation.isAllowed || hasContentBeingAdded,
        [isNodeInProject, ptCreation.isAllowed, hasContentBeingAdded]
    )

    const addContentAfterSibling = useCallback(
        (sibling: DataNode) => {
            if (!sibling) {
                console.warn('Cannot add content: sibling node is required')
                return
            }

            // Check if trying to add Article after a MOPT article
            const isSiblingMOPTArticle = isDataNodeMOPTArticle(sibling)

            if (isAddDisabled || sibling.isHidden) {
                if (!ptCreation.isAllowed) {
                    const treePathPrefix = parentTreePath?.split('|')?.[0] || ''
                    const isAssembliesView = treePathPrefix.length > 2
                    const viewType = isAssembliesView
                        ? 'Assemblies'
                        : 'Divisions'

                    ptCreation.showRestrictionNotification(viewType)
                }
                return false
            }

            // Block adding Article after MOPT Article
            if (isSiblingMOPTArticle) {
                setMOPTDialogOpen(true, 'ADD')
                return false
            }

            try {
                switch (sibling.type) {
                    case 'Article': {
                        // #2716145: an article that has been expanded (children fetched)
                        // and came back empty should receive a first paragraph inside it,
                        // not a sibling article.
                        const isLoadedAndEmpty =
                            expandedKeys.includes(String(sibling.key)) &&
                            sibling.children?.length === 0
                        if (isLoadedAndEmpty) {
                            addFirstParagraphToArticle(sibling)
                        } else {
                            addArticleAfterSibling(sibling)
                        }
                        break
                    }
                    case 'Paragraph':
                        addParagraphAfterSibling(sibling)
                        break
                    default:
                        throw new Error(
                            `Unable to add content: Unsupported node type '${sibling.type}'`
                        )
                }
            } catch (error) {
                console.error('Failed to add content after sibling:', error)
                throw error
            }
        },
        [
            addArticleAfterSibling,
            addParagraphAfterSibling,
            addFirstParagraphToArticle,
            isAddDisabled,
            setMOPTDialogOpen,
            ptCreation.isAllowed,
            ptCreation.showRestrictionNotification,
            parentTreePath,
            expandedKeys,
        ]
    )

    const canAddContent = useCallback(
        (sibling: DataNode, from?: 'AddIcon' | 'KeyboardShortcut'): boolean => {
            if (!sibling) return false

            // Block content creation during save operations to prevent race conditions
            if (isSaveOperationInProgress) {
                return false
            }

            if (from !== 'AddIcon' && sibling.type === 'Article') {
                console.warn(
                    'Cannot add content: Invalid sibling node or permissions'
                )
                return false
            }

            return sibling.type === 'Article' || sibling.type === 'Paragraph'
        },
        [isSaveOperationInProgress]
    )

    const getTooltipText = useCallback(
        (node?: DataNode) => {
            // Check if node is a MOPT article
            const isMOPTArticleNode = isDataNodeMOPTArticle(node)

            return getTooltipTextForState(
                isAddDisabled,
                hasContentBeingAdded,
                ptCreation.isAllowed,
                isMOPTArticleNode
            )
        },
        [isAddDisabled, hasContentBeingAdded, ptCreation.isAllowed]
    )

    return {
        addContentAfterSibling,
        canAddContent,
        isAddDisabled,
        hasContentBeingAdded,
        getTooltipText,
    }
}
